import type { NavItem, NavSection } from '@/navigation/adminNav';

/**
 * Users module sidebar — mirrors layouts/admin/partials/_sidebar_users.blade.php.
 * Permission keys match Helpers::module_permission_check gates in Blade.
 * Frontend visibility only — Laravel remains the authorization boundary.
 */

export type UsersNavPermission =
  | 'cashback'
  | 'deliveryman'
  | 'customer_management'
  | 'custom_role'
  | 'employee';

export type UsersNavItem = NavItem & {
  /** module_permission_check key — hide when the session user lacks it */
  permission?: UsersNavPermission;
};

export type UsersNavSection = Omit<NavSection, 'items'> & {
  items: UsersNavItem[];
  /** Section header is shown only when at least one child is visible */
  permission?: UsersNavPermission;
};

const USERS_MODULE_NAV: UsersNavSection[] = [
  {
    id: 'users-overview',
    title: '',
    icon: 'layout-dashboard',
    items: [
      {
        label: 'User Overview',
        to: '/users',
        icon: 'layout-dashboard',
        keywords: ['dashboard', 'overview', 'users'],
      },
    ],
  },
  {
    id: 'users-promotion',
    title: 'Promotion Management',
    icon: 'megaphone',
    permission: 'cashback',
    items: [
      {
        label: 'Cashback',
        to: '/cashback',
        icon: 'coins',
        permission: 'cashback',
        keywords: ['promotion', 'cashback'],
      },
    ],
  },
  {
    id: 'users-deliveryman',
    title: 'Deliveryman Management',
    icon: 'bike',
    permission: 'deliveryman',
    items: [
      {
        label: 'Vehicles Category',
        to: '/vehicles',
        icon: 'car',
        permission: 'deliveryman',
        keywords: ['vehicle', 'category', 'type'],
      },
      {
        label: 'Deliveryman List',
        to: '/deliverymen',
        icon: 'bike',
        permission: 'deliveryman',
        keywords: ['list', 'drivers', 'couriers', 'add', 'create', 'manage', 'pending', 'applications'],
      },
      {
        label: 'Reviews',
        to: '/deliverymen/reviews',
        icon: 'star',
        permission: 'deliveryman',
        keywords: ['reviews', 'ratings', 'feedback'],
      },
    ],
  },
  {
    id: 'users-customer',
    title: 'Customer Management',
    icon: 'contact-round',
    permission: 'customer_management',
    items: [
      {
        label: 'Customers',
        to: '/customers',
        icon: 'contact-round',
        permission: 'customer_management',
        keywords: ['customers', 'clients', 'users'],
      },
      {
        label: 'Customer Wallet',
        to: '/customers/wallet',
        icon: 'wallet-cards',
        permission: 'customer_management',
        keywords: ['wallet', 'fund', 'bonus'],
        children: [
          {
            label: 'Add Fund',
            to: '/customers/wallet',
            search: { action: 'add-fund' },
            depth: 1,
            permission: 'customer_management',
          },
          {
            label: 'Report',
            to: '/customers/wallet',
            depth: 1,
            permission: 'customer_management',
          },
          {
            label: 'Bonus',
            to: '/wallet-bonus',
            depth: 1,
            permission: 'customer_management',
          },
        ],
      },
      {
        label: 'Customer Loyalty Point',
        to: '/customers/loyalty',
        icon: 'award',
        permission: 'customer_management',
        keywords: ['loyalty', 'points'],
        children: [
          {
            label: 'Report',
            to: '/customers/loyalty',
            depth: 1,
            permission: 'customer_management',
          },
        ],
      },
      {
        label: 'Subscribed Mail List',
        to: '/customers/subscribed',
        icon: 'mail',
        permission: 'customer_management',
        keywords: ['subscribed', 'newsletter', 'mail'],
      },
      {
        label: 'Contact Messages',
        to: '/contacts',
        icon: 'message',
        permission: 'customer_management',
        keywords: ['contact', 'messages', 'inbox'],
      },
    ],
  },
  {
    id: 'users-employee',
    title: 'Employee Management',
    icon: 'briefcase-business',
    items: [
      {
        label: 'Employee Role',
        to: '/roles',
        icon: 'shield-check',
        permission: 'custom_role',
        keywords: ['role', 'permission', 'rbac'],
      },
      {
        label: 'Employees',
        to: '/employees',
        icon: 'briefcase-business',
        permission: 'employee',
        keywords: ['staff', 'employees'],
        children: [
          {
            label: 'Add New',
            to: '/employees/new',
            depth: 1,
            permission: 'employee',
          },
          {
            label: 'List',
            to: '/employees',
            depth: 1,
            permission: 'employee',
          },
        ],
      },
    ],
  },
];

/** Path prefixes that belong to the Users module (Blade `admin/users*`). */
export const USERS_MODULE_PATH_PREFIXES = [
  '/users',
  '/customers',
  '/deliverymen',
  '/vehicles',
  '/employees',
  '/roles',
  '/contacts',
  '/cashback',
  '/wallet-bonus',
];

export function isUsersModulePath(pathname: string): boolean {
  return USERS_MODULE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function hasPermission(
  permission: UsersNavPermission | undefined,
  granted: Set<string> | undefined,
): boolean {
  if (!permission) return true;
  if (!granted) return true;
  return granted.has(permission) || granted.has('all') || granted.has('*');
}

export function filterUsersNav(
  sections: UsersNavSection[],
  granted?: Set<string> | null,
): UsersNavSection[] {
  return sections
    .map((section) => {
      if (!hasPermission(section.permission, granted ?? undefined)) {
        return { ...section, items: [] };
      }
      const items = section.items
        .filter((item) => hasPermission(item.permission, granted ?? undefined))
        .map((item) => ({
          ...item,
          children: item.children?.filter((child) =>
            hasPermission((child as UsersNavItem).permission, granted ?? undefined),
          ),
        }));
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

export { USERS_MODULE_NAV };
